import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Search, ArrowRight, Loader2, Zap } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { trackEvent, AnalyticsEvents } from "@/lib/analytics";
import { getEnvironmentParam } from "@/lib/environment";

const EmissionsEstimator = () => {
  const { t } = useLanguage();
  const [query, setQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [error, setError] = useState("");
  const [isVisible, setIsVisible] = useState(false);
  const [animatedPlaceholder, setAnimatedPlaceholder] = useState("");
  const typingIndexRef = useRef(0);

  // API response state
  const [companyName, setCompanyName] = useState("");
  const [totalEmissions, setTotalEmissions] = useState(0);
  const [scopeValues, setScopeValues] = useState<{ scope1: number; scope2: number; scope3: number } | null>(null);

  const placeholderText = t("estimator.placeholder");

  // Typing animation effect with loop and blinking cursor
  const [showCursor, setShowCursor] = useState(true);

  useEffect(() => {
    if (query) return;

    let timeout: ReturnType<typeof setTimeout>;

    const typeChar = () => {
      if (typingIndexRef.current <= placeholderText.length) {
        setAnimatedPlaceholder(placeholderText.slice(0, typingIndexRef.current));
        typingIndexRef.current++;
        timeout = setTimeout(typeChar, 50);
      } else {
        // Pause at the end, then restart
        timeout = setTimeout(() => {
          typingIndexRef.current = 0;
          setAnimatedPlaceholder("");
          timeout = setTimeout(typeChar, 300);
        }, 2000);
      }
    };

    timeout = setTimeout(typeChar, 300);
    return () => clearTimeout(timeout);
  }, [placeholderText, query]);

  // Blinking cursor effect
  useEffect(() => {
    if (query) return;
    const interval = setInterval(() => {
      setShowCursor((prev) => !prev);
    }, 530);
    return () => clearInterval(interval);
  }, [query]);

  // Reset animation when language changes
  useEffect(() => {
    typingIndexRef.current = 0;
    setAnimatedPlaceholder("");
  }, [placeholderText]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      {
        threshold: 0.2,
      },
    );
    const section = document.getElementById("emissions-estimator");
    if (section) observer.observe(section);
    return () => observer.disconnect();
  }, []);
  const validateCorporateId = (id: string): boolean => {
    const digitsOnly = id.replace(/\D/g, "");
    // Must be exactly 13 digits
    if (digitsOnly.length !== 13) return false;
    // Must contain only digits
    return /^\d{13}$/.test(digitsOnly);
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim()) return;
    setError("");

    const cleanId = query.replace(/\D/g, "");

    if (!validateCorporateId(query)) {
      setError(t("estimator.error"));
      setShowResult(false);
      return;
    }

    // Demo mode: show demo data for 0000000000000
    if (cleanId === "0000000000000") {
      setCompanyName("ABC Corporation Co., Ltd.");
      setTotalEmissions(12500);
      setScopeValues(null); // Use fallback percentages for demo
      setShowResult(true);
      return;
    }

    setIsLoading(true);

    try {
      // Call edge function to proxy the API request (bypasses CORS)
      const env = getEnvironmentParam();

      // Workaround: invoke doesn't support query params, so we call directly
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/estimate-emissions?id=${encodeURIComponent(cleanId)}&env=${env}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
            'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          },
        }
      );

      const responseData = await response.json();

      if (!response.ok || responseData.code === 'NOT_FOUND') {
        setError(t("estimator.error"));
        setShowResult(false);
        return;
      }

      if (responseData.code === 'INVALID_FORMAT') {
        setError(t("estimator.error"));
        setShowResult(false);
        return;
      }

      if (!responseData.success || !responseData.result || !responseData.company_name) {
        setError(t("estimator.error"));
        setShowResult(false);
        return;
      }

      setCompanyName(responseData.company_name);
      // Convert from kgCO2e to tCO2e (divide by 1000)
      setTotalEmissions(responseData.result / 1000);
      
      // Check if API returned scope breakdown
      if (responseData.scope_1 !== undefined && responseData.scope_2 !== undefined && responseData.scope_3 !== undefined) {
        const s1 = parseFloat(responseData.scope_1) / 1000; // Convert to tCO2e
        const s2 = parseFloat(responseData.scope_2) / 1000;
        const s3 = parseFloat(responseData.scope_3) / 1000;
        setScopeValues({ scope1: s1, scope2: s2, scope3: s3 });
      } else {
        setScopeValues(null); // Use fallback percentages
      }
      
      setShowResult(true);
    } catch (error) {
      console.error('Emissions estimation error:', error);
      setError(t("estimator.networkError") !== "estimator.networkError" 
        ? t("estimator.networkError") 
        : "Unable to connect. Please try again.");
      setShowResult(false);
    } finally {
      setIsLoading(false);
    }
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Remove any non-digit characters and limit to 13 digits
    const value = e.target.value.replace(/\D/g, "").slice(0, 13);
    setQuery(value);
    if (error) setError("");
  };

  const isValidLength = query.length === 13;
  
  // Calculate scope breakdown - use actual values if available, otherwise fallback to fixed percentages
  const scopeBreakdown = (() => {
    if (scopeValues) {
      // Use actual scope values from API
      const total = scopeValues.scope1 + scopeValues.scope2 + scopeValues.scope3;
      const calcPercentage = (value: number) => total > 0 ? Math.round((value / total) * 100) : 0;
      return [
        { label: "Scope 1", value: Math.round(scopeValues.scope1), percentage: calcPercentage(scopeValues.scope1), color: "#064E3B" },
        { label: "Scope 2", value: Math.round(scopeValues.scope2), percentage: calcPercentage(scopeValues.scope2), color: "#10B981" },
        { label: "Scope 3", value: Math.round(scopeValues.scope3), percentage: calcPercentage(scopeValues.scope3), color: "#6EE7B7" },
      ];
    }
    // Fallback: fixed percentages (15/25/60)
    return [
      { label: "Scope 1", value: Math.round(totalEmissions * 0.15), percentage: 15, color: "#064E3B" },
      { label: "Scope 2", value: Math.round(totalEmissions * 0.25), percentage: 25, color: "#10B981" },
      { label: "Scope 3", value: Math.round(totalEmissions * 0.6), percentage: 60, color: "#6EE7B7" },
    ];
  })();
  return (
    <section
      id="emissions-estimator"
      className={`pt-10 sm:pt-12 lg:pt-16 ${showResult ? "pb-20 lg:pb-28" : "pb-16 lg:pb-20"} bg-background relative overflow-hidden transition-all duration-500`}
    >
      {/* Background gradient overlay */}
      <div
        className="absolute inset-0 bg-gradient-to-b from-secondary/30 via-secondary/50 to-transparent pointer-events-none"
        style={{
          height: "70%",
        }}
      />
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 -left-20 w-72 h-72 bg-primary/10 rounded-full blur-3xl animate-pulse" />
        <div
          className="absolute bottom-1/4 -right-20 w-96 h-96 bg-accent/10 rounded-full blur-3xl animate-pulse"
          style={{
            animationDelay: "1s",
          }}
        />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/5 rounded-full blur-3xl" />
      </div>

      {/* Orbiting particles around search area */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] md:w-[600px] h-[150px] md:h-[200px]">
          {[...Array(4)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1.5 md:w-2 h-1.5 md:h-2 bg-primary/40 rounded-full hidden sm:block"
              style={{
                animation: `orbit-mobile ${8 + i * 2}s linear infinite`,
                animationDelay: `${i * -1.5}s`,
                left: "50%",
                top: "50%",
              }}
            />
          ))}
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header with animation */}
        <div
          className={`text-center max-w-3xl mx-auto mb-12 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-cta mb-4">
            {t("estimator.title1")}
            <br />
            <span className="text-gradient">{t("estimator.title2")}</span>
          </h2>
        </div>

        {/* Combined Search and Result Card */}
        <div
          className={`max-w-4xl mx-auto transition-all duration-700 delay-100 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <div className="relative group">
            {/* Animated glow effect */}
            <div className={`absolute -inset-1 bg-gradient-to-r from-primary via-accent to-primary rounded-2xl sm:rounded-3xl blur-lg opacity-30 group-hover:opacity-50 transition-opacity duration-500 ${showResult ? "" : "animate-pulse"}`} />

            <div
              className={`relative bg-card border-2 rounded-2xl sm:rounded-3xl shadow-xl overflow-hidden transition-all duration-500 ${error ? "border-destructive" : "border-primary/30 group-hover:border-primary/50"}`}
            >
              {/* Search Bar */}
              <form onSubmit={handleSearch} className="relative">
                <div className={`flex items-center h-14 sm:h-16 px-3 sm:px-4 ${showResult ? "border-b border-border/50" : ""}`}>
                  <div className="flex items-center justify-center w-8 sm:w-10 flex-shrink-0">
                    <Search className="w-5 sm:w-6 h-5 sm:h-6 text-primary" />
                  </div>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={13}
                    value={query}
                    onChange={handleInputChange}
                    placeholder={query ? "" : `${animatedPlaceholder}${showCursor ? "|" : ""}`}
                    className="flex-1 px-2 sm:px-3 py-0 bg-transparent text-foreground placeholder:text-muted-foreground focus:outline-none text-lg min-w-0"
                  />
                  <button
                    type="submit"
                    disabled={isLoading || !isValidLength}
                    aria-label={isLoading ? t("estimator.loading") || "Loading..." : t("estimator.submitQuery") || "Submit corporate ID query"}
                    className="mx-1 sm:mx-2 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                  >
                    {isLoading ? <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" /> : <ArrowRight className="w-5 h-5" aria-hidden="true" />}
                  </button>
                </div>
                {error && <p className="text-destructive text-sm py-3 px-4 text-center animate-fade-in">{error}</p>}
              </form>

              {/* Result Section - Slides in when showResult is true */}
              <div
                className={`overflow-hidden transition-all duration-500 ease-out ${showResult ? "max-h-[600px] opacity-100" : "max-h-0 opacity-0"}`}
              >
                <div className="pt-4 pb-4 px-4 sm:pt-6 sm:pb-8 sm:px-8 md:pt-8 md:pb-10 md:px-10 lg:pt-10 lg:pb-12 lg:px-12">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8 lg:gap-10 items-start">
                    {/* Emissions Result */}
                    <div className="text-center md:text-left flex flex-col">
                      {/* Company Name - Moved to top */}
                      <div className="mb-3 sm:mb-6">
                        <p className="text-xs sm:text-sm font-medium text-muted-foreground mb-1 sm:mb-2">
                          {t("estimator.companyName")}
                        </p>
                        <p className="text-sm sm:text-lg md:text-xl font-semibold text-foreground">{companyName || "—"}</p>
                      </div>

                      {/* Result Label */}
                      <p className="text-xs sm:text-sm font-medium text-muted-foreground mb-1 sm:mb-2">
                        {t("estimator.resultLabel")}
                      </p>
                      
                      {/* Emissions Number */}
                      <div className="flex items-baseline justify-center md:justify-start gap-1">
                        <span className="text-2xl sm:text-3xl md:text-4xl font-bold text-primary">
                          {Math.round(totalEmissions).toLocaleString()}
                        </span>
                        <span className="text-xs sm:text-sm md:text-base text-muted-foreground">{t("estimator.unit")}</span>
                      </div>
                      
                      {/* Tree Equivalent */}
                      <div className="mt-3 sm:mt-4">
                        <p className="text-xs sm:text-sm font-medium text-muted-foreground mb-1 sm:mb-2">
                          {t("estimator.treeEquivalent")}
                        </p>
                        <div className="flex items-baseline justify-center md:justify-start gap-1">
                          <span className="text-xl sm:text-2xl md:text-3xl font-bold text-primary">
                            {Math.round(totalEmissions * 46).toLocaleString()}
                          </span>
                          <span className="text-xs sm:text-sm md:text-base text-muted-foreground">{t("estimator.trees")}</span>
                        </div>
                      </div>
                    </div>

                    {/* Scope Breakdown Pie Chart */}
                    <div className="bg-secondary/50 rounded-xl sm:rounded-2xl p-5 sm:p-6 md:p-7">
                      <div className="flex items-center gap-2 mb-4 sm:mb-5">
                        <Zap className="w-4 sm:w-5 h-4 sm:h-5 text-accent" aria-hidden="true" />
                        <p className="font-semibold text-foreground text-sm sm:text-base">Breakdown Scope 1,2,3</p>
                      </div>

                      <div className="flex items-center gap-4 sm:gap-6">
                        {/* Pie Chart */}
                        <div className="relative w-28 h-28 sm:w-32 sm:h-32 md:w-36 md:h-36 lg:w-40 lg:h-40 flex-shrink-0">
                          <svg viewBox="0 0 100 100" className="w-full h-full">
                            {(() => {
                              let cumulativePercentage = 0;
                              return scopeBreakdown.map((scope, index) => {
                                const startAngle = (cumulativePercentage / 100) * 360;
                                const sweepAngle = showResult ? (scope.percentage / 100) * 360 : 0;
                                cumulativePercentage += scope.percentage;

                                const startRad = (startAngle - 90) * (Math.PI / 180);
                                const endRad = (startAngle + sweepAngle - 90) * (Math.PI / 180);

                                const x1 = 50 + 40 * Math.cos(startRad);
                                const y1 = 50 + 40 * Math.sin(startRad);
                                const x2 = 50 + 40 * Math.cos(endRad);
                                const y2 = 50 + 40 * Math.sin(endRad);

                                const largeArcFlag = sweepAngle > 180 ? 1 : 0;

                                const pathD =
                                  sweepAngle >= 360
                                    ? `M 50 10 A 40 40 0 1 1 49.99 10 Z`
                                    : `M 50 50 L ${x1} ${y1} A 40 40 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;

                                return (
                                  <path
                                    key={index}
                                    d={pathD}
                                    fill={scope.color}
                                    className="transition-all duration-1000 ease-out"
                                    style={{ transitionDelay: `${index * 200}ms` }}
                                  />
                                );
                              });
                            })()}
                          </svg>
                        </div>

                        {/* Legend */}
                        <div className="flex-1 min-w-0 space-y-3 sm:space-y-4">
                          {scopeBreakdown.map((scope, index) => (
                            <div key={index} className="flex items-center gap-2 sm:gap-3">
                              <div
                                className="w-3 h-3 rounded-full flex-shrink-0"
                                style={{ backgroundColor: scope.color }}
                              />
                              <span className="text-xs sm:text-sm text-foreground font-medium whitespace-nowrap">{scope.label}</span>
                              <div className="text-right ml-auto flex-shrink-0">
                                <span className="text-xs sm:text-sm font-semibold text-foreground">
                                  {scope.value.toLocaleString()}
                                </span>
                                <span className="text-[10px] sm:text-xs text-muted-foreground ml-1">
                                  ({scope.percentage}%)
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      <p className="text-[10px] sm:text-xs text-muted-foreground mt-4 sm:mt-5">
                        {t("estimator.disclaimer")}
                      </p>
                    </div>
                  </div>
                  
                  {/* CTA Button - Centered between both sections */}
                  <div className="flex justify-center mt-10 sm:mt-12">
                    <Button
                      asChild
                      size="default"
                      className="group shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 text-sm sm:text-base"
                    >
                      <a 
                        href="/contact-us"
                        onClick={() => trackEvent(AnalyticsEvents.DOWNLOAD_EMISSIONS_SUMMARY, {
                          company_name: companyName,
                          total_emissions: totalEmissions,
                          corporate_id: query,
                        })}
                      >
                        {t("estimator.ctaButton")}
                        <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                      </a>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
export default EmissionsEstimator;
